import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject, of, timer } from 'rxjs';
import { UntypedFormGroup, Validators, NgForm, FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService, PersonalService, FeedMessageService, LoadingService, UserPinSessionService, UserPinAuthService, CustomValidators, UserPinService, showSuccess, showErrorAndWarning } from '@services/index.service';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { catchError, finalize, map, take, tap, timeout } from 'rxjs/operators';
import { Consumer, Response, LoginStep, AngularPaths, AuthUserPinType, LocalStorageKeys, UserPinConfig, CreateUserPinRequest, CodeUserPinRequest, UserSession } from '@models/index.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-set-reset-user-pin',
    templateUrl: './set-reset-user-pin.component.html',
    styleUrls: ['./set-reset-user-pin.component.scss'],
    standalone: false
})
export class SetResetUserPinComponent implements OnInit {
  public theme = environment.THEME;
  public sendingPin: boolean = false;
  public sendingPinCode: boolean = false;
  // Custom login screen settings.
  public customImgSrc: string;
  public customText: string;
  // Login steps management.
  public loginStep: LoginStep;
  public loadingCustomData = false;
  public loadingLogin = false;
  public loadingLoginCert = false;
  public isMobile: boolean;
  public ssoLoginButtons = [];
  public ssoLoginClickedButton = '';
  public certificateDomainsConfigured: boolean = false;
  @ViewChild('password') public set focusPassword(password: ElementRef) {
    if (password) {
      password.nativeElement.focus();
      this._cdRef.detectChanges();
    }
  }
  public hideCode: boolean = true;
  public hideUserPinNew: boolean = true;
  public hideUserPinRepeat: boolean = true;
  public currentConsumer: Consumer = Consumer.GLOBAL_PERSONAL_AREA;
  public nextStep: AuthUserPinType;
  public userPinConfig: UserPinConfig;
  @ViewChild('loginForm') loginForm: NgForm;
  @ViewChild('normalUserPinForm') normalUserPinForm: NgForm;
  @ViewChild('codeUserPinForm') codeUserPinForm: NgForm;
  normalUserPinFormGroup: UntypedFormGroup;
  codeUserPinFormGroup: UntypedFormGroup;
  alreadySendCode: boolean = false;
  countDown = new BehaviorSubject<number>(0);
  count = 30;

  constructor(
    public dialog: MatDialog,
    public translate: TranslateService,
    private router: Router,
    public _utils: UtilsService,
    private _userPinSession: UserPinSessionService,
    private _titleService: Title,
    private _userPinServ: UserPinService,
    private _notify: FeedMessageService,
    private _cdRef: ChangeDetectorRef
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.isMobile = environment.platform === 'mobile';
    // this.globalFormGroup = new FormGroup({
    this.normalUserPinFormGroup = new FormGroup({
        newPin: new FormControl<string>('', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]),
        newPinConfirm: new FormControl<string>('', [Validators.required, Validators.minLength(4), Validators.maxLength(8)])
      }, {validators: CustomValidators.fieldsMatch(['newPin', 'newPinConfirm'])});

      this.codeUserPinFormGroup = new FormGroup({
        code: new FormControl<string>('', [Validators.required]),
        newPin: new FormControl<string>('', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]),
        newPinConfirm: new FormControl<string>('', [Validators.required, Validators.minLength(4), Validators.maxLength(8)])
      }, {validators: CustomValidators.fieldsMatch(['newPin', 'newPinConfirm'])});
    // });
  }

  public ngOnInit() {
    this.translate.get('GLOBAL.ACCOUNT-EDIT-INFO-3-PIN').subscribe(res => {
      this._titleService.setTitle(`${res} - ${this.theme}`);
    });
    this.dialog.closeAll();
    this.currentConsumer = Consumer.GLOBAL_PERSONAL_AREA;

    if (window.location.href.includes(`/${AngularPaths.CONFIG_USER_PIN}/`)) {
      const userPinRedirectId = localStorage.getItem(LocalStorageKeys.USER_PIN_REDIRECT_ID);
      const currentSession: UserSession = this._utils.getLocalStorage(LocalStorageKeys.USER_PIN_SESSION);
      this.userPinConfig = this._utils.parseUserPreferenceConfig(userPinRedirectId);
      if (environment.production) {
        window.history.replaceState({}, '',`${window.location.origin}/personal/${AngularPaths.CONFIG_USER_PIN}/`);
      } else {
        window.history.replaceState({}, '',`${window.location.origin}/${AngularPaths.CONFIG_USER_PIN}/`);
      }
      if (this.userPinConfig) {
        if (currentSession?.userData.domainType) {
          this.userPinConfig.dt = currentSession.userData.domainType
        }
        if (this.userPinConfig.op && (this.userPinConfig.op === AuthUserPinType.SET)) {
          this.nextStep = AuthUserPinType.SET;
        }
        if (this.userPinConfig.op && (this.userPinConfig.op === AuthUserPinType.RESET)) {
          this.nextStep = AuthUserPinType.RESET;
        }
        this.loginStep = LoginStep.CONFIG_USER_PIN;
      }
    } else {
      this.authRedirect();
    }
  }

  get normalNewPin() {
    return this.normalUserPinFormGroup.get('newPin');
  }
  get normalNewPinConfirm() {
    return this.normalUserPinFormGroup.get('newPinConfirm');
  }
  get code() {
    return this.codeUserPinFormGroup.get('code');
  }
  get codeNewPin() {
    return this.codeUserPinFormGroup.get('newPin');
  }
  get codeNewPinConfirm() {
    return this.codeUserPinFormGroup.get('newPinConfirm');
  }

  public createUserPin() {
    const pinRequest: CreateUserPinRequest = {
      userPin: this.normalUserPinFormGroup.get('newPin').value,
      action: this.userPinConfig.op,
    };
    this._createUserPinRequest(pinRequest).subscribe();
  }

  private _createUserPinRequest(userPinRequest: CreateUserPinRequest) {
    this.sendingPin = true;
    return this._userPinServ.createUserPin(userPinRequest).pipe(
      tap((res: Response<any>) => {
        this.normalUserPinForm.resetForm();
        this._notify.feedBackResult(res.messageType, res.message, showSuccess);
        this.loginStep = LoginStep.FINISH;
        this.close();
      }),
      catchError(err => {
        this.normalUserPinForm.resetForm();
        return of(err);
      }),
      finalize(() => {
        this.sendingPin = false;
      })
    );
  }

  private _sendUserPinCodeRequest() {
    this.sendingPinCode = true;
    return this._userPinServ.sendEmailCode().pipe(
      tap((res: Response<any>) => {
        if (res.data) {
          this._notify.feedBackResult(res.messageType, res.message, showSuccess);
        } else {
          this._notify.feedBackResult(res.messageType, res.message, showErrorAndWarning);
        }
        this.alreadySendCode = res.data || false;
      }),
      catchError(err => {
        return of(err);
      }),
      finalize(() => {
        this.sendingPinCode = false;
      })
    );
  }

  public codeUserPin() {
    const pinRequest: CodeUserPinRequest = {
      code: this.codeUserPinFormGroup.get('code').value,
      userPin: this.codeUserPinFormGroup.get('newPin').value,
      action: this.userPinConfig.op,
    };
    this._codeUserPinRequest(pinRequest).subscribe();
  }

  public sendPinCode() {
    this._sendUserPinCodeRequest().subscribe();
  }

  public resendPinCode() {
    this.startCountdown(this.count);
    this._sendUserPinCodeRequest().subscribe();
  }

  private startCountdown(seconds: number) {
    this.countDown.next(seconds); // Inicia el contador
    timer(0, 1000)
      .pipe(
        take(seconds), // Detiene el timer después de `seconds` emisiones
        map((elapsed) => seconds - elapsed - 1) // Calcula el tiempo restante
      )
      .subscribe({
        next: (remaining) => this.countDown.next(remaining),
        complete: () => this.countDown.next(0), // Asegura que el contador se detenga en 0
      });
  }

  private _codeUserPinRequest(userPinRequest: CodeUserPinRequest) {
    this.sendingPin = true;
    return this._userPinServ.createUserPin(userPinRequest).pipe(
      tap((res: Response<any>) => {
        this.codeUserPinForm.resetForm();
        this.loginStep = LoginStep.FINISH;
        this._notify.feedBackResult(res.messageType, res.message, showSuccess);
        this.close();
      }),
      catchError(err => {
        this.codeUserPinForm.resetForm();
        return of(err);
      }),
      finalize(() => {
        this.sendingPin = false;
      })
    );
  }

  public authRedirect() {
    localStorage.removeItem(LocalStorageKeys.USER_PIN_SESSION);
    const userPinRedirectId = localStorage.getItem(LocalStorageKeys.USER_PIN_REDIRECT_ID);
    if (userPinRedirectId) {
      this.router.navigate([`/${AngularPaths.USER_PIN}/${userPinRedirectId}`], { replaceUrl: true });
    } else {
      this.redirectPersonal();
    }
  }

  public close() {
    this._userPinSession.removeBrowserSession();
    setTimeout(() => {
      this.redirectPersonal();
    }, 10000);
  }

  public redirectPersonal() {
    if (environment.production) {
      window.location.href = `${window.location.origin}/personal/${AngularPaths.CONFIG_USER_PIN}/`;
    } else {
      window.location.href = `${window.location.origin}/${AngularPaths.CONFIG_USER_PIN}/`;
    }
    window.location.reload();
  }
}
